/**
 * Every URL from `import.meta.env.VITE_*` — **no hardcoded origin anywhere**.
 *
 * These are compiled into the bundle, so they are public-safe values only
 * (APP_DEVELOPMENT_GUIDE §10). A secret here ships to every browser.
 *
 * Deployed, the API has its own hostname (FiveOX ADR-0056):
 * `api.uat.fiveox.ai` and `api.fiveox.ai` serve `/api/*`, and
 * `accounts.<env>` keeps the sign-in surface and serves no `/api/` route.
 * **Local development is unchanged** — `localhost:8100` still serves both.
 */

const env = import.meta.env

function required(name: string, value: string | undefined, fallback: string): string {
  if (value) return value
  if (env.PROD) {
    // A production bundle built without its config is the failure that looks
    // like a backend outage. Say so at boot, loudly, rather than 404ing later.
    console.error(`[prodesk] ${name} is not set; falling back to ${fallback}`)
  }
  return fallback
}

const EDGE = 'http://localhost:8100'

export const AUTH_API = required('VITE_AUTH_API', env.VITE_AUTH_API, `${EDGE}/api/auth`)
export const ACCESS_API = required('VITE_ACCESS_API', env.VITE_ACCESS_API, `${EDGE}/api/access`)
export const APPMGMT_API = required('VITE_APPMGMT_API', env.VITE_APPMGMT_API, `${EDGE}/api/apps`)
export const COMM_API = required('VITE_COMM_API', env.VITE_COMM_API, `${EDGE}/api/comm`)
export const USERS_API = required('VITE_USERS_API', env.VITE_USERS_API, `${EDGE}/api/users`)
export const ORGS_API = required('VITE_ORGS_API', env.VITE_ORGS_API, `${EDGE}/api/orgs`)

/**
 * `core-domain-whitelisting`, browser-facing (Wave 1A).
 *
 * The firm's own domains, with their verification state and the DNS records
 * core prescribes, come from **`${DOMAINS_API}` itself** — core's contract,
 * read with the signed-in user's session and the org header. Two reasons it is
 * read here rather than proxied by prodesk-service: the route is user-scoped,
 * so a proxy would have to impersonate the user; and the service-to-service
 * surface answers for **bound** hosts only, which cannot tell "no domain" from
 * "being verified" (see `app/platform/domains.py`).
 *
 * Our own service still owns the *gate*: `GET /api/v1/firm/domain` is the
 * fail-closed binding read, and it is what decides whether the site may
 * publish. This URL only makes the waiting legible.
 *
 * **The base is the collection, not a prefix to build on.** The edge maps
 * `/api/domains` straight onto `domains-service:8019/api/v1/domains`, so the
 * service's own `/api/v1/domains` prefix is already spent — appending it again
 * 404s. Read from the running edge on 2026-09-10 after the container pass did
 * exactly that (CLAUDE.md §8 rule 2).
 */
export const DOMAINS_API = required('VITE_DOMAINS_API', env.VITE_DOMAINS_API, `${EDGE}/api/domains`)

/** ProDesk's own service. 8150 locally — allocated, not chosen (ADR-0003). */
export const SERVICE_API = required('VITE_PRODESK_API', env.VITE_PRODESK_API, 'http://localhost:8150')

/**
 * **ProDoc's public API**, where `@fiveox/prodoc-ui` sends the client panel's
 * requests (Wave 28E, ADR-0055 §6 and §8): `http://localhost:8170` locally,
 * `https://prodoc.uat.fiveox.ai` on UAT.
 *
 * **No default, unlike every URL above.** The documents panel becomes ProDoc's
 * library only for an org that has moved, and a localhost fallback baked into a
 * deployed bundle would point a moved firm's staff at their own machine. So an
 * unset value is `null`, and `lib/prodoc.ts` then asks ProDesk's service, which
 * knows its environment's ProDoc (C-4's I-5, `prodoc_api_url`). With neither,
 * the panel stays ProDesk's own.
 */
export const PRODOC_API_URL: string | null = String(env.VITE_PRODOC_API_URL ?? '').trim() || null

export const ACCOUNTS_URL = required('VITE_ACCOUNTS_URL', env.VITE_ACCOUNTS_URL, 'http://localhost:3102')
export const LAUNCHER_URL = required('VITE_LAUNCHER_URL', env.VITE_LAUNCHER_URL, 'http://localhost:3104')
export const CONSOLE_URL = required('VITE_CONSOLE_URL', env.VITE_CONSOLE_URL, 'http://localhost:3106')
export const STORE_URL = required('VITE_STORE_URL', env.VITE_STORE_URL, 'http://localhost:3107')
export const HELP_URL = required('VITE_HELP_URL', env.VITE_HELP_URL, 'http://localhost:3108')

/**
 * The platform website: since the store deleted its browse page (fiveox ADR-0053, T8.3) the
 * website is the only place a plan is chosen, and the store's own `itemPricingUrl` points here.
 * `PLANS_URL` is where every *View plans* in this app goes (C27 item 12, UAT report #25) — the
 * `${STORE_URL}/catalog/<slug>` the paywall used to link 404s.
 */
export const WEBSITE_URL = required('VITE_WEBSITE_URL', env.VITE_WEBSITE_URL, 'http://localhost:3100')
export const plansUrl = (slug: string): string =>
  `${WEBSITE_URL}/products/${encodeURIComponent(slug)}/pricing`

/**
 * Staff surfaces, for the waffle's **internal** mode (ADR-0054). Keyed by
 * *registry* slug (`core-admin`), not app slug — that is what
 * `GET /api/access/staff/me/access` answers with.
 *
 * An external user never sees these; the resolver only asks for them when the
 * token says `user_type: internal`, and an entry with no URL is dropped rather
 * than rendered dead.
 */
export const ADMIN_URL = env.VITE_ADMIN_URL ?? 'http://localhost:3103'
export const CRM_URL = env.VITE_CRM_URL ?? 'http://localhost:3105'
export const SUPPORT_URL = env.VITE_SUPPORT_URL ?? 'http://localhost:3109'

/** The registered OAuth `client_id` (APP_DEVELOPMENT_GUIDE §6, registration 1). */
export const SSO_CLIENT_ID = env.VITE_SSO_CLIENT_ID ?? 'prodesk'

/** The API-stable slug, in ONE place. The platform proposes renaming it (its ADR-0078). */
export const APP_SLUG = env.VITE_APP_SLUG ?? 'ca-erp'
export const APP_NAME = 'ProDesk'

/**
 * The origin allow-list, compared as a **parsed origin**, never as a string
 * prefix (FiveOX ADR-0048). `https://evil.com/?x=https://fiveox.ai` has
 * the right prefix and is not the right origin.
 */
export const PLATFORM_ORIGINS: string[] = String(env.VITE_PLATFORM_ORIGINS ?? '')
  .split(',')
  .map((origin: string) => origin.trim())
  .filter(Boolean)

export function isAllowedOrigin(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin === window.location.origin) return true
    return PLATFORM_ORIGINS.some((allowed: string) => {
      try {
        return new URL(allowed).origin === parsed.origin
      } catch {
        return false
      }
    })
  } catch {
    return false
  }
}
